// Global registry that tracks every in-flight API request via AbortController.
// cancelAll() is called by the router guard on route change so all pending
// requests from the previous page are aborted automatically.

const activeRequests = new Map<symbol, AbortController>();

function mergeAbortSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any([a, b]);
  }
  // Fallback for browsers without AbortSignal.any support
  const controller = new AbortController();
  if (a.aborted || b.aborted) {
    controller.abort();
    return controller.signal;
  }
  a.addEventListener('abort', () => controller.abort(), { once: true });
  b.addEventListener('abort', () => controller.abort(), { once: true });
  return controller.signal;
}

export const requestRegistry = {
  /** Register a new request and return its unique key and AbortSignal. */
  register(): { key: symbol; signal: AbortSignal } {
    const key = Symbol();
    const controller = new AbortController();
    activeRequests.set(key, controller);
    return { key, signal: controller.signal };
  },

  /** Remove a completed request from the registry (called after success or real error). */
  deregister(key: symbol): void {
    activeRequests.delete(key);
  },

  /** Abort every in-flight request. Called by the router guard on navigation. */
  cancelAll(): void {
    activeRequests.forEach((controller) => controller.abort());
    activeRequests.clear();
  },
};

/**
 * Create a registry entry for a request.
 * If the model already carries a signal (from a component-level AbortController),
 * merge it so either side can cancel the request independently.
 */
export function buildRequestSignal(existingSignal?: AbortSignal): { key: symbol; signal: AbortSignal } {
  const { key, signal: registrySignal } = requestRegistry.register();
  const signal = existingSignal ? mergeAbortSignals(registrySignal, existingSignal) : registrySignal;
  return { key, signal };
}
